/**
 * Notice body rich-text helpers (v1 allowlist).
 *
 * Allowed: p, br, strong/b, em/i, u, ul/ol/li
 * Legacy plain text (no tags) stays plain — render with pre-wrap.
 */

export const NOTICE_BODY_VISIBLE_MAX = 1000;
export const NOTICE_BODY_VISIBLE_MIN = 10;

const TAG_DETECT_RE = /<\/?[a-z][\s\S]*>/i;

/** True when body looks like HTML rather than legacy plain text. */
export function isPlainNoticeBody(body: string): boolean {
  return !TAG_DETECT_RE.test(body || "");
}

/**
 * Strip tags to visible plain text (push, feed clamp, char limits).
 * Works in browser and Node (no DOM required for the common path).
 */
export function htmlToPlainText(html: string): string {
  if (!html) return "";
  if (isPlainNoticeBody(html)) {
    return html.replace(/\r\n/g, "\n").trimEnd();
  }

  let text = html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*p\s*>/gi, "\n")
    .replace(/<\/\s*li\s*>/gi, "\n")
    .replace(/<\/\s*(div|h[1-6]|tr|blockquote)\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

  text = text
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text;
}

export function noticeBodyVisibleLength(body: string): number {
  return htmlToPlainText(body || "").length;
}

export function noticeBodyHasVisibleContent(body: string): boolean {
  return noticeBodyVisibleLength(body) > 0;
}

/** Tags/attrs allowed after sanitize (DOMPurify ALLOWED_*). */
export const NOTICE_BODY_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
] as const;

export const NOTICE_BODY_ALLOWED_ATTR: string[] = [];

/**
 * Normalize TipTap / paste HTML into a compact allowlisted string.
 * Call after DOMPurify (or in tests with already-clean HTML).
 */
export function normalizeNoticeBodyHtml(html: string): string {
  if (!html) return "";
  const trimmed = html.trim();
  if (!trimmed) return "";

  // TipTap often wraps everything in <p>…</p>
  if (isPlainNoticeBody(trimmed)) {
    return trimmed;
  }

  // Collapse empty paragraphs TipTap leaves behind
  return trimmed
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/<p><br\s*\/?><\/p>/gi, "")
    .trim();
}

/** Escape plain text so it reads as typed inside notice body HTML. */
export function escapeNoticeBodyText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Escape plain text into a single paragraph for the editor when hydrating
 * legacy notices (so TipTap can edit them without showing raw newlines oddly).
 */
export function plainTextToNoticeBodyHtml(plain: string): string {
  const text = (plain || "").replace(/\r\n/g, "\n");
  if (!text.trim()) return "";

  return text
    .split("\n")
    .map((line) => `<p>${escapeNoticeBodyText(line) || "<br>"}</p>`)
    .join("");
}
